import { DatePipe, NgTemplateOutlet, TitleCasePipe } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, ElementRef, Signal, computed, inject, signal, viewChild } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { ExportDataComponent } from '@app/shared/components/export-data/export-data.component';
import { InitialPipe } from '@app/shared/pipes/initial.pipe';
import { ProjectService } from '@app/shared/services/project.service';
import { RECORD_SHOW_OPTIONS } from '@app/shared/utils/constants';
import { copyToClipboard, exportToCSV, onImageError } from '@app/shared/utils/helper';
import { Committee } from 'lfx-pcc';
import { MessageService } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { DialogService } from 'primeng/dynamicdialog';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import { catchError, distinctUntilChanged, filter, forkJoin, map, of, startWith, switchMap, take } from 'rxjs';
import { CommitteeAuditFiltersPanelComponent } from './components/committee-audit-filters-panel/committee-audit-filters-panel.component';
import { CommitteeAuditEntry, CommitteeAuditState, CommitteeFilterState, FilterUser, GroupedAuditLog } from './interfaces/committee-audit-log.interface';
import { applyFilters, getColumnMap, groupAuditLogs, processTableData } from './utils/committee-audit-log.utils';

@Component({
  selector: 'lfx-committee-audit-log',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    ButtonModule,
    InitialPipe,
    AvatarModule,
    SkeletonModule,
    PaginatorModule,
    TooltipModule,
    DatePipe,
    TitleCasePipe,
    CommitteeAuditFiltersPanelComponent,
  ],
  templateUrl: './committee-audit-log.component.html',
  styleUrl: './committee-audit-log.component.scss',
  providers: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommitteeAuditLogComponent {
  public scrollContainer = viewChild<ElementRef>('scrollContainer');

  // Injected services
  private router = inject(Router);
  private projectService = inject(ProjectService);
  private messageService = inject(MessageService);
  private datePipe = inject(DatePipe);
  private dialogService = inject(DialogService);

  // Mutable UI state signals
  public currentPage = signal<number>(0);
  public pageSize = signal<number>(50);
  public isFiltersVisible = signal<boolean>(false);
  public period = signal<string>('all');

  // Filter selection signals (user-mutable, two-way bound with child component)
  public dateFrom = signal<Date | null>(null);
  public dateTo = signal<Date | null>(null);
  public selectedTypes = signal<string[]>([]);
  public selectedActions = signal<string[]>([]);
  public selectedCommittees = signal<string[]>([]);
  public selectedMembers = signal<string[]>([]);
  public selectedUsers = signal<FilterUser[]>([]);
  public selectedFields = signal<string[]>([]);

  // Route-derived signals
  public readonly committeeId: Signal<string | null> = this.initializeCommitteeId();

  // Declarative audit state — loading/error/data/committee derived from a single observable stream
  private readonly auditParams = this.initializeAuditParams();
  private readonly auditState: Signal<CommitteeAuditState> = this.initializeAuditState();
  public readonly loading = computed(() => this.auditState().loading);
  public readonly hasError = computed(() => this.auditState().error);
  private readonly auditData = computed(() => this.auditState().data);
  public readonly committee = computed(() => this.auditState().committee);

  // Project
  public readonly projectName = computed(() => this.projectService.currentProjectSig()?.Name ?? '');

  // Audit logs derived from auditData
  public readonly auditLogs = computed(() => this.auditData()?.sortedAuditLogs ?? []);

  // Filter options derived from auditData
  public readonly filterUsers = computed(() => this.auditData()?.filterUsers ?? []);
  public readonly filterTypes = computed(() => this.auditData()?.filterTypes ?? []);
  public readonly filterActions = computed(() => this.auditData()?.filterActions ?? []);
  public readonly filterCommittees = computed(() => this.auditData()?.filterCommittees ?? []);
  public readonly filterMembers = computed(() => this.auditData()?.filterMembers ?? []);
  public readonly filterFields = computed(() => this.auditData()?.filterFields ?? []);

  // Filtered and paginated data chain
  public readonly filteredAuditLogs: Signal<CommitteeAuditEntry[]> = this.initializeFilteredAuditLogs();
  private readonly clampedPage = this.initializeClampedPage();
  public readonly paginatedLogs: Signal<CommitteeAuditEntry[]> = this.initializePaginatedLogs();
  public readonly groupedPaginatedLogs: Signal<GroupedAuditLog[]> = computed(() => groupAuditLogs(this.paginatedLogs()));
  public readonly paginatorFirst = computed(() => this.clampedPage() * this.pageSize());

  // Filter state
  public readonly hasActiveFilters: Signal<boolean> = this.initializeHasActiveFilters();
  public readonly activeFilterCount: Signal<number> = this.initializeActiveFilterCount();

  // Constants
  public readonly rangeOptions = RECORD_SHOW_OPTIONS;
  public readonly rowsPerPageOptions = [10, 25, 50, 100];
  public readonly onImageError = onImageError;

  public toggleFilters(): void {
    this.isFiltersVisible.update((v) => !v);
  }

  public resetPage(): void {
    this.currentPage.set(0);
  }

  public onPageChange(event: PaginatorState): void {
    this.currentPage.set(event.page ?? 0);
    this.pageSize.set(event.rows ?? 50);
    this.scrollToTop();
  }

  public onCopyToClipboard(text: string): void {
    copyToClipboard(text)
      .then(() => {
        this.messageService.add({ severity: 'success', summary: 'Copied', detail: 'Successfully copied to clipboard', life: 3000 });
      })
      .catch(() => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to copy to clipboard', life: 3000 });
      });
  }

  public onExport(): void {
    const project = this.projectService.currentProjectSig();
    if (!project) return;

    const exportFields = ['Date', 'User', 'Action', 'Committee', 'Type', 'Members', 'Field', 'From', 'To'];
    const fieldOptions = exportFields.map((field) => ({ label: field, value: field, checked: true }));
    this.dialogService
      .open(ExportDataComponent, {
        header: 'Export Committees Audit Log',
        dismissableMask: true,
        data: {
          projectId: project.ID,
          action: `export-${project.Slug}-${this.committee()?.Name || 'committees'}-audit-log`,
          fieldOptions,
          project,
        },
      })
      ?.onClose.pipe(take(1))
      .subscribe((fields) => {
        if (fields && fields.length > 0) {
          this.exportAuditLog(fields);
        }
      });
  }

  // ── Declarative initialization methods ──────────────────────────────

  private initializeClampedPage(): Signal<number> {
    return computed(() => {
      const maxPage = Math.max(0, Math.ceil(this.filteredAuditLogs().length / this.pageSize()) - 1);
      return Math.min(this.currentPage(), maxPage);
    });
  }

  private initializePaginatedLogs(): Signal<CommitteeAuditEntry[]> {
    return computed(() => {
      const start = this.clampedPage() * this.pageSize();
      const end = start + this.pageSize();
      return this.filteredAuditLogs().slice(start, end);
    });
  }

  private initializeAuditParams() {
    return computed(() => ({
      projectId: this.projectService.currentProjectSig()?.ID ?? null,
      committeeId: this.committeeId(),
      period: this.period(),
    }));
  }

  private initializeCommitteeId(): Signal<string | null> {
    const currentId = this.getCommitteeIdFromRouterState();
    return toSignal(
      this.router.events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        map(() => this.getCommitteeIdFromRouterState()),
        distinctUntilChanged(),
        startWith(currentId)
      ),
      { initialValue: currentId }
    );
  }

  private getCommitteeIdFromRouterState(): string | null {
    let route = this.router.routerState.root;
    while (route.firstChild) {
      route = route.firstChild;
      const id = route.snapshot.paramMap.get('committee');
      if (id && id.trim() !== '') return id;
    }
    return null;
  }

  private initializeAuditState(): Signal<CommitteeAuditState> {
    return toSignal(
      toObservable(this.auditParams).pipe(
        switchMap(({ projectId, committeeId, period }) => {
          const project = this.projectService.currentProjectSig();
          if (!projectId || !project) return of<CommitteeAuditState>({ loading: true, error: false, data: null, committee: null });

          const params = new HttpParams({ fromObject: { period } });
          const isSpecificCommittee = !!committeeId;

          const auditLogs$ = isSpecificCommittee
            ? this.projectService.getCommitteeAuditLog(project.ID, committeeId!, params)
            : this.projectService.getCommitteesAuditLog(project.ID, params);

          const committee$ = isSpecificCommittee
            ? this.projectService.getCommittee(project.ID, committeeId!).pipe(catchError(() => of(null as Committee | null)))
            : of(null as Committee | null);

          return forkJoin({ logs: auditLogs$, committee: committee$ }).pipe(
            map(
              ({ logs, committee }): CommitteeAuditState => ({
                loading: false,
                error: false,
                data: logs?.length ? processTableData(logs) : null,
                committee,
              })
            ),
            catchError((error) => {
              const detail = isSpecificCommittee
                ? 'Failed to get committee audit log. Please try again or contact support.'
                : 'Failed to get committees audit log. Please try again or contact support.';
              console.error('Failed to load committee audit log:', error);
              this.messageService.add({ severity: 'error', summary: 'Error', detail, data: error, key: 'support' });
              return of<CommitteeAuditState>({ loading: false, error: true, data: null, committee: null });
            }),
            startWith<CommitteeAuditState>({ loading: true, error: false, data: null, committee: null })
          );
        })
      ),
      { initialValue: { loading: true, error: false, data: null, committee: null } }
    );
  }

  private initializeFilteredAuditLogs(): Signal<CommitteeAuditEntry[]> {
    return computed(() => {
      const data = this.auditData();
      if (!data) return [];
      const filterState: CommitteeFilterState = {
        dateFrom: this.dateFrom(),
        dateTo: this.dateTo(),
        selectedTypes: this.selectedTypes(),
        selectedActions: this.selectedActions(),
        selectedCommittees: this.committeeId() ? [] : this.selectedCommittees(),
        selectedMembers: this.selectedMembers(),
        selectedUsers: this.selectedUsers(),
        selectedFields: this.selectedFields(),
      };
      return applyFilters(data.sortedAuditLogs, filterState);
    });
  }

  private initializeHasActiveFilters(): Signal<boolean> {
    return computed(() => {
      return !!(
        this.period() !== 'all' ||
        this.dateFrom() ||
        this.dateTo() ||
        this.selectedTypes().length > 0 ||
        this.selectedActions().length > 0 ||
        (!this.committeeId() && this.selectedCommittees().length > 0) ||
        this.selectedMembers().length > 0 ||
        this.selectedUsers().length > 0 ||
        this.selectedFields().length > 0
      );
    });
  }

  private initializeActiveFilterCount(): Signal<number> {
    return computed(() => {
      let count = 0;
      if (this.period() !== 'all') count++;
      if (this.dateFrom() || this.dateTo()) count++;
      count += this.selectedTypes().length;
      count += this.selectedActions().length;
      if (!this.committeeId()) count += this.selectedCommittees().length;
      count += this.selectedMembers().length;
      count += this.selectedUsers().length;
      count += this.selectedFields().length;
      return count;
    });
  }

  private scrollToTop(): void {
    this.scrollContainer()?.nativeElement?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private exportAuditLog(selectedColumns: string[]): void {
    const project = this.projectService.currentProjectSig();
    if (!project) return;

    const columnMap = getColumnMap(this.datePipe);
    const data = this.filteredAuditLogs().map((log) => selectedColumns.map((column) => columnMap[column]?.(log) || '-'));
    exportToCSV(data, `${project.Slug}-${this.committee()?.Name || 'committees'}-audit-log`, selectedColumns);
  }
}
